import { useNavigate } from 'react-router-dom'
import { session } from '../lib/api'
import { useApi, useConnection, useGeolocation, useOutboxCount } from '../lib/hooks'
import { clock, money, num } from '../lib/format'
import { Banner, Empty, ErrorNote, IconWifiOff, Loading, Panel } from '../ui/kit'
import { FieldHeader } from './FieldShell'

export interface RouteStop {
  seq: number
  id: string
  status: string
  plannedKg: number | null
  plannedValue: number | null
  farmer: {
    id: string; code: string; name: string; msisdn: string
    kycStatus: string; lat: number | null; lng: number | null; portraitUrl: string | null
  }
  crop: { id: string; name: string } | null
  lot: { id: string; code: string; farmgateKg: number; value: number; status: string } | null
  payment: {
    id: string; status: string; amount: number
    settledAt: string | null; failureReason: string | null
  } | null
}

export interface TodayRoute {
  id: string; code: string; planDate: string; status: string
  zone: { id: string; name: string }
  depot: { id: string; name: string }
  agent: { id: string; name: string } | null
  priceBoardId: string | null
  plannedKg: number; plannedValue: number; payoutCutoffAt: string | null
  startedAt: string | null; completedAt: string | null
  progress: { stopsPlanned: number; stopsDone: number; collectedKg: number; collectedValue: number }
  stops: RouteStop[]
}

export function Today() {
  const nav = useNavigate()
  const { online, offlineMinutes } = useConnection()
  const queued = useOutboxCount()
  const { coords, denied } = useGeolocation()
  const user = session.user

  const route = useApi<TodayRoute>('/routes/today', { cacheKey: 'route.today' })

  const r = route.data
  const done = r?.progress.stopsDone ?? 0
  const total = r?.progress.stopsPlanned ?? 0
  const progressPct = total > 0 ? (done / total) * 100 : 0

  const greeting = new Date().getHours() < 12 ? 'Habari' : 'Habari ya jioni'
  const firstName = user?.fullName.split(' ')[0] ?? ''

  return (
    <>
      <FieldHeader />

      <div className="field-greet">
        <h1>{greeting}, {firstName}</h1>
        <p>
          {new Date().toLocaleDateString('en-KE', { weekday: 'long', day: 'numeric', month: 'short' })}
          {r && <> · {r.zone.name} · route {r.code}</>}
        </p>
      </div>

      <div className="field-chips">
        <span className={`chip ${coords ? 'on' : denied ? 'off' : ''}`}>
          ◎ {coords ? `GPS ±${Math.round(coords.accuracy)} m` : denied ? 'GPS off' : 'GPS…'}
        </span>
        <span className={`chip ${online ? 'on' : 'off'}`}>
          {online ? '▲ Online' : '▼ Offline'}
        </span>
        {queued > 0 && <span className="chip">{queued} queued</span>}
      </div>

      <div className="field-body">
        {!online && (
          <Banner
            tone="warn"
            icon={<IconWifiOff size={16} />}
            title={`Offline${offlineMinutes > 0 ? ` — ${offlineMinutes} min` : ''}`}
          >
            Weighing and grading carry on. Payments queue on this device and send themselves the
            moment you are back on a signal.
          </Banner>
        )}

        {route.stale && online && (
          <Banner tone="info" title="This may be out of date">
            The server could not be reached just now. This is what was cached at the start of the day.
          </Banner>
        )}

        {route.error && !route.data && <ErrorNote error={route.error} onRetry={route.reload} />}
        {route.loading && !route.data && <Panel><Loading rows={4} /></Panel>}

        {!route.loading && !route.data && !route.error && (
          <Empty title="No route assigned today">
            Ops plans a route to give you a farmer list, the day's prices and a payout budget. Ask for
            one, or check back after the morning plan is published.
          </Empty>
        )}

        {r && (
          <>
            <div className="brand-card">
              <span className="micro">Today's collection</span>
              <div className="bc-title">
                {r.zone.name}
                <span>{r.status === 'in_progress' ? 'in progress' : r.status}</span>
              </div>

              <div className="bc-grid">
                <div>
                  <b className="num">{total}</b>
                  <i>Farmers</i>
                </div>
                <div>
                  <b className="num">{num(r.plannedKg)} kg</b>
                  <i>Expected</i>
                </div>
                <div>
                  <b className="num">{money(r.plannedValue, { compact: true })}</b>
                  <i>Budget</i>
                </div>
              </div>

              <div>
                <div className="spread" style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--on-brand-dim)' }}>
                  <span>Collected {done} / {total}</span>
                  <span className="num">
                    {num(r.progress.collectedKg, 1)} kg · {money(r.progress.collectedValue, { compact: true })}
                  </span>
                </div>
                <div className="bc-bar" style={{ marginTop: 6 }}>
                  <i style={{ width: `${progressPct}%` }} />
                </div>
              </div>

              {r.payoutCutoffAt && (
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--on-brand-dim)', textAlign: 'center' }}>
                  Payout window closes {clock(new Date(r.payoutCutoffAt).toTimeString().slice(0, 8))} —
                  farmers are paid the same day
                </p>
              )}
            </div>

            <Panel
              title="Route farmers"
              meta={`${done} collected · ${total - done} ahead`}
            >
              <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
                {r.stops.map((s) => (
                  <StopRow key={s.id} stop={s} onOpen={() => nav(`/m/weigh/${s.id}`)} />
                ))}
                {r.stops.length === 0 && (
                  <Empty title="No stops on this route yet" />
                )}
              </div>
            </Panel>
          </>
        )}
      </div>
    </>
  )
}

function StopRow({ stop, onOpen }: { stop: RouteStop; onOpen: () => void }) {
  const paid = stop.payment && ['queued', 'authorised', 'sending', 'sent', 'confirmed'].includes(stop.payment.status)
  const failed = stop.payment?.status === 'failed'
  const collected = !!stop.lot

  const state = failed ? 'fail' : collected ? 'done' : 'pending'
  const mark = failed ? '!' : collected ? '✓' : String(stop.seq)

  return (
    <button className="stop-row" onClick={onOpen}>
      <span className={`stop-state ${state}`}>{mark}</span>
      <span className="stop-who">
        <b>{stop.farmer.name}</b>
        <i>
          {stop.crop?.name ?? 'Crop not set'}
          {stop.lot ? ` · ${Number(stop.lot.farmgateKg).toFixed(1)} kg` : ` · ${num(stop.plannedKg, 0)} kg expected`}
        </i>
      </span>
      <span className="stop-amt">
        <b className={failed ? 'crit-t' : paid ? 'ok-t' : ''}>
          {stop.lot
            ? money(stop.lot.value, { currency: false })
            : money(stop.plannedValue, { currency: false })}
        </b>
        <span>
          {failed
            ? 'Payment failed · retry'
            : stop.payment?.settledAt
              ? `Paid ${new Date(stop.payment.settledAt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })}`
              : paid
                ? 'Payment queued'
                : collected
                  ? 'Awaiting payment'
                  : 'Estimate'}
        </span>
      </span>
    </button>
  )
}
